import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import {
  updateGHPayment,
  GH_PAYMENT_METHODS,
} from "../../../services/guestHouseServicesApi";

export default function EditPaymentModal({
  open,
  onClose,
  record,
  paymentItem,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [formData, setFormData] = useState({
    paid_amount: "",
    paymentMethod: "UPI",
    status: "Approved",
    notes: "",
  });

  useEffect(() => {
    if (paymentItem) {
      setFormData({
        paid_amount: paymentItem.paid_amount ?? "",
        paymentMethod: paymentItem.paymentMethod || "UPI",
        status: paymentItem.status || "Approved",
        notes: paymentItem.notes || "",
      });
    }
  }, [paymentItem]);

  if (!open || !record || !paymentItem) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loadingRef.current) return;
    const amt = Number(formData.paid_amount);
    if (!amt || amt <= 0) {
      Swal.fire("Validation Error", "Please provide a valid amount.", "warning");
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      await updateGHPayment(
        record._id || record.guestHouseServiceId,
        paymentItem._id,
        {
          paid_amount: amt,
          paymentMethod: formData.paymentMethod,
          status: formData.status,
          notes: formData.notes,
        }
      );

      Swal.fire({
        title: "Payment Updated!",
        text: `Payment entry has been updated.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating payment:", err);
      Swal.fire("Error", typeof err === "string" ? err : "Failed to update payment", "error");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="gh-modal-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "calc(100vh - 48px)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
        },
      }}
    >
      <div
        className="gh-modal-header"
        style={{
          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="fa fa-pencil fs-5" />
          <div>
            <h5 className="mb-0 fw-bold" style={{ fontSize: "16px" }}>
              Edit Payment Entry
            </h5>
            <small style={{ color: "rgba(255,255,255,0.85)" }}>
              {paymentItem.payment_Date} • ₹{paymentItem.paid_amount}
            </small>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <DialogContent style={{ padding: "20px", flex: "1 1 auto", overflowY: "auto" }}>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">
                Paid Amount (₹) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="any"
                required
                name="paid_amount"
                value={formData.paid_amount}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">
                Payment Method <span className="text-danger">*</span>
              </label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                className="form-select"
              >
                {GH_PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label small fw-bold text-dark">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div className="col-12">
              <label className="form-label small fw-bold text-dark">Notes / Updated Remarks</label>
              <textarea
                rows="2"
                name="notes"
                placeholder="e.g. Updated receipt amount, verified by accountant"
                value={formData.notes}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light btn-sm px-3"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-success btn-sm px-4 fw-bold"
              disabled={loading}
              style={{ backgroundColor: "#059669", borderColor: "#059669" }}
            >
              {loading ? <i className="fa fa-spinner fa-spin" /> : "Save Payment"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
